import React, { useState } from 'react';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid';
import { CheckboxField } from 'candidate_profile_redesign/shared/form/Fields';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import Form, {
  useFormContext,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import { TrackLastPage } from './shared/track_last_page';
import MultiStepDataEntryModal from '@grnhse/seedling/lib/birch/components/modals/multi_step_data_entry_modal';
import { useFetchModal } from 'candidate_profile_redesign/shared/form/useFetchModal';
import { standardRequestWithFlash } from 'shared/utils/request';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import DropdownArrowIcon from '@grnhse/seedling/lib/birch/icons/dropdown_arrow';
import { CompactRow } from '@grnhse/seedling/lib/birch/layouts/compact_row';
import Collapsible from '@grnhse/seedling/lib/birch/components/collapsible';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import { useCandidateProfile } from 'candidate_profile_redesign/store';
import { NeutralTag } from '@grnhse/seedling/lib/birch/components/tags';
import { withLocalePrefix } from 'shared/utils/translation';
import SelectJobStageFields from './shared/select_job_stage_fields';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';

const t = withLocalePrefix('candidate_profile_redesign.modals.add_to_job');

interface ModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  modalPath: string;
  submitPath: string;
  preselectedHiringPlan?: Item;
  onSuccess?: (selectedHiringPlanId: string) => void;
}

interface Attachment {
  id: number;
  type: 'resume' | 'cover_letter' | 'other';
  display_name: string;
  private: boolean;
}

interface Application {
  id: number;
  name: string;
}

interface FormProps {
  hiring_plan: Item;
  stage: Item;
  attachments?: {
    string: string;
  };
  isLastPage: boolean;
}

type AttachmentResponse = [Application, Attachment[]];

const PageOne = ({ hiringPlansPath }: { hiringPlansPath?: string }) => {
  return (
    <MultiStepDataEntryModal.Page>
      <Stack>
        <Body>{t('body_1')}</Body>
        <SelectJobStageFields
          hiringPlansPath={hiringPlansPath}
          automationFlow="add"
        />
      </Stack>
    </MultiStepDataEntryModal.Page>
  );
};

const AttachmentsRow = ({
  application,
  attachments,
  currentApplicationId,
}: {
  application: Application;
  attachments: Array<Attachment>;
  currentApplicationId: number;
}) => {
  const isCurrentApplication = application.id === currentApplicationId;
  const [isExpanded, setIsExpanded] = useState(isCurrentApplication);

  const { setValue, getValues } = useFormContext();

  const onSelectAll = (checked: boolean) => {
    const entries = Object.entries(getValues('attachments'));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const resume = entries.find(([_key, type]) => type === 'resume');
    const cover_letter = entries.find(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([_key, type]) => type === 'cover_letter'
    );

    attachments.forEach((attachment) => {
      // if we're about the set a new resume, uncheck the old one
      if (checked && attachment.type === 'resume' && resume) {
        setValue(`attachments.${resume[0]}`, false);
        setValue(`select_all_${resume[0].split('_')[1]}`, false);
      }
      // if we're about to set a new cover letter, uncheck the old one
      if (checked && attachment.type === 'cover_letter' && cover_letter) {
        setValue(`attachments.${cover_letter[0]}`, false);
        setValue(`select_all_${cover_letter[0].split('_')[1]}`, false);
      }

      setValue(
        `attachments.attachment_${application.id}_${attachment.id}`,
        checked ? attachment.type : false
      );
    });
  };

  const onCheckboxChange = (checked: boolean, attachment: Attachment) => {
    const values = getValues('attachments');

    // uncheck any other cover_letter or resume document and its corresponding select all
    if (
      checked &&
      (attachment.type === 'cover_letter' || attachment.type === 'resume')
    ) {
      const entries = Object.entries(values);
      entries.forEach(([key, type]) => {
        if (type === attachment.type) {
          setValue(`attachments.${key}`, false);
          setValue(`select_all_${key.split('_')[1]}`, false);
        }
      });
    }

    const allAreChecked =
      checked &&
      attachments.every(
        (a) =>
          attachment.id === a.id ||
          values[`attachment_${application.id}_${a.id}`]
      );

    setValue(`select_all_${application.id}`, allAreChecked);
  };

  return (
    <Stack
      spacing={isExpanded ? 2 : 0}
      data-provides="attachment-row"
      data-provides-for={application.id}
    >
      <CompactRow
        key={application.id}
        // @ts-expect-error - no onClick
        onClick={() => setIsExpanded(!isExpanded)}
        data-provides="row-header"
      >
        <div style={{ rotate: `${isExpanded ? 0 : 270}deg` }}>
          <DropdownArrowIcon />
        </div>
        <Body medium>{application.name}</Body>
        {isCurrentApplication && <NeutralTag text={t('viewing_now')} />}
      </CompactRow>

      <Collapsible isOpen={isExpanded} id={`attachments-for-${application.id}`}>
        <Grid container>
          <Grid item sx={{ paddingLeft: 4 }}>
            {attachments.length > 1 && (
              <CheckboxField
                label={t('select_all')}
                compact
                name={`select_all_${application.id}`}
                onChange={onSelectAll}
                value={'all_selected'}
              />
            )}
            {attachments.map((attachment) => (
              <CheckboxField
                compact
                key={attachment.id}
                name={`attachments.attachment_${application.id}_${attachment.id}`}
                value={attachment.type}
                onChange={(checked: boolean) =>
                  onCheckboxChange(checked, attachment)
                }
                helperText={
                  t(`documents.${attachment.type}`) +
                  (attachment.private ? t('documents.private') : '')
                }
                label={attachment.display_name}
                isPiiBlurred
              />
            ))}
          </Grid>
        </Grid>
      </Collapsible>
    </Stack>
  );
};

const PageTwo = ({
  attachments,
  applicationId,
}: {
  attachments?: AttachmentResponse;
  applicationId: number;
}) => {
  return (
    <MultiStepDataEntryModal.Page>
      <Stack spacing={3}>
        <Body>{t('body_2')}</Body>
        <Stack divider={<Divider />} spacing={2}>
          {/* @ts-expect-error - must have a '[Symbol.iterator]()' method that returns an iterator*/}
          {attachments?.map(([application, appAttachments]) => (
            <AttachmentsRow
              key={application.id}
              application={application}
              attachments={appAttachments}
              currentApplicationId={applicationId}
            />
          ))}
        </Stack>
      </Stack>
    </MultiStepDataEntryModal.Page>
  );
};

const ModalFooter = ({
  onDismiss,
  submitting,
}: {
  onDismiss: () => void;
  submitting: boolean;
}) => {
  const {
    formState: { isValid },
  } = useFormContext();

  return (
    <MultiStepDataEntryModal.Footer>
      {({
        isFirstPage,
        isLastPage,
      }: {
        isFirstPage: boolean;
        isLastPage: boolean;
      }) => (
        <Grid
          container
          item
          spacing={2}
          justify="space-between"
          alignItems={'center'}
          wrap={'nowrap'}
        >
          <TrackLastPage isLastPage={isLastPage} />
          <Grid item justify="flex-start">
            {!isFirstPage && (
              <MultiStepDataEntryModal.BackButton>
                {t('back')}
              </MultiStepDataEntryModal.BackButton>
            )}
          </Grid>
          <Grid item>
            <CompactRow>
              <MultiStepDataEntryModal.CancelButton onCancel={onDismiss}>
                {t('cancel')}
              </MultiStepDataEntryModal.CancelButton>
              {isLastPage ? (
                <MultiStepDataEntryModal.SaveButton
                  disabled={submitting || !isValid}
                  loading={submitting}
                >
                  {t('submit')}
                </MultiStepDataEntryModal.SaveButton>
              ) : (
                <MultiStepDataEntryModal.NextButton disabled={!isValid}>
                  {t('next')}
                </MultiStepDataEntryModal.NextButton>
              )}
            </CompactRow>
          </Grid>
        </Grid>
      )}
    </MultiStepDataEntryModal.Footer>
  );
};

const AddToJobModal = ({
  isOpen,
  onDismiss,
  modalPath,
  submitPath,
  preselectedHiringPlan,
  onSuccess,
}: ModalProps) => {
  const { data } = useFetchModal<{
    attachments: AttachmentResponse;
    hiring_plans_path: string;
  }>(modalPath, isOpen);

  const {
    state: { applicationId },
  } = useCandidateProfile();

  const [submitting, setSubmitting] = useState(false);

  const onAddCandidateSave = async (form: FormProps) => {
    setSubmitting(true);

    const payload = new FormData();
    payload.append('hiring_plan_id', form.hiring_plan.value);
    payload.append('initial_stage_id', form.stage.value);
    Object.entries(form.attachments || {}).forEach(([key, value]) => {
      if (value) {
        payload.append('attachments[]', key.split('_')[2]);
      }
    });

    // tranfer job modal uses the same path do we need to specify
    // what action we're taking
    payload.append('type', 'add');

    try {
      const { data: submitResponse } = await standardRequestWithFlash.post(
        submitPath,
        payload
      );
      onSuccess?.(form.hiring_plan.value);
      onDismiss();
      if (submitResponse.goto) {
        window.location.href = submitResponse.goto;
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form
      onSubmit={onAddCandidateSave}
      defaultValues={
        preselectedHiringPlan
          ? { hiring_plan: preselectedHiringPlan }
          : undefined
      }
    >
      <MultiStepDataEntryModal
        size={'medium'}
        isOpen={isOpen}
        onDismiss={onDismiss}
        onSubmit={() => null}
        ariaLabel={'add-to-job-modal'}
        data-provides={'add-to-job-modal'}
        numberOfPages={data?.attachments?.length ? 2 : 1}
      >
        <MultiStepDataEntryModal.Header onClick={onDismiss}>
          {t('header')}
        </MultiStepDataEntryModal.Header>

        <MultiStepDataEntryModal.Body>
          <PageOne hiringPlansPath={data?.hiring_plans_path} />
          <PageTwo
            attachments={data?.attachments}
            applicationId={applicationId}
          />
        </MultiStepDataEntryModal.Body>
        <ModalFooter onDismiss={onDismiss} submitting={submitting} />
      </MultiStepDataEntryModal>
    </Form>
  );
};

export default AddToJobModal;
